/**
 * components/landing/ai-platform/sections/Architecture.jsx — §4.3.
 *
 * Vertical AI stack diagram — 8 layers from User → Response.  Each
 * layer expands on click to show its detail.  A subtle downward arrow
 * connects the layers to imply flow.
 */

import React, { useState } from 'react'
import clsx from 'clsx'
import {
  User, Bot, Wand2, BookOpen, Compass, ShieldCheck, Layers, MessageSquare,
  ChevronDown, ArrowDown, Sparkles,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { AI_ARCH } from '../data'
import { TONE } from '../_shared'

const ICONS = { User, Bot, Wand2, BookOpen, Compass, ShieldCheck, Layers, MessageSquare }

export default function Architecture() {
  const [openId, setOpenId] = useState('rag')
  return (
    <section className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-[0.4]"
        style={{ background: 'radial-gradient(50% 40% at 50% 20%, rgba(168,85,247,0.10), transparent 70%)' }} />
      <Container size="wide" className="flex flex-col gap-12">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>AI Architecture</Eyebrow>
          <SectionHeading
            align="center"
            title={<>How every AI request <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">actually flows.</span></>}
            subtitle="Eight discrete stages between the operator asking and the grounded answer landing. Each stage has a single responsibility, is separately governable, and is separately upgradable."
          />
        </div>

        <div className="flex flex-col gap-2 max-w-3xl mx-auto w-full">
          {AI_ARCH.map((layer, idx) => {
            const tone = TONE[layer.tone] || TONE.orange
            const Icon = ICONS[layer.iconKey] || Sparkles
            const open = openId === layer.id
            return (
              <React.Fragment key={layer.id}>
                <div className={clsx(
                  'relative rounded-2xl border transition-all overflow-hidden',
                  open ? 'border-white/20 bg-gradient-to-b from-white/[0.05] to-white/[0.01]'
                       : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.03]',
                )}>
                  {open && (
                    <div className="pointer-events-none absolute inset-0 opacity-70"
                      style={{ background: `radial-gradient(70% 45% at 10% 0%, rgba(${tone.rgb},0.14), transparent 70%)` }} />
                  )}
                  <button
                    onClick={() => setOpenId(open ? null : layer.id)}
                    aria-expanded={open}
                    className="relative w-full flex items-center gap-4 p-4 md:p-5 text-left"
                  >
                    <span
                      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border"
                      style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}
                    >
                      <Icon strokeWidth={1.8} style={{ width: 16, height: 16 }} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="font-mono text-[10.5px] uppercase tracking-wider" style={{ color: tone.hex }}>Layer 0{layer.num}</span>
                      </div>
                      <div className="text-base md:text-lg font-semibold text-white leading-tight">{layer.label}</div>
                    </div>
                    <ChevronDown className={clsx('h-4 w-4 text-zinc-500 shrink-0 transition-transform', open && 'rotate-180')} />
                  </button>
                  {open && (
                    <div className="relative px-5 pb-5 md:px-6 md:pb-6">
                      <div className="pl-13">
                        <p className="text-sm text-zinc-300 leading-relaxed max-w-2xl">{layer.detail}</p>
                      </div>
                    </div>
                  )}
                </div>

                {idx < AI_ARCH.length - 1 && (
                  <div className="flex justify-center py-0.5">
                    <ArrowDown className="h-3.5 w-3.5 text-zinc-600" />
                  </div>
                )}
              </React.Fragment>
            )
          })}
        </div>
      </Container>
    </section>
  )
}
